/**
 * Pure date helpers for the Workbench schedule calendar. Kept free of React
 * and the marketing client so they can be unit-tested in isolation. All dates
 * are handled in the viewer's LOCAL timezone (the calendar shows local time;
 * the API speaks ISO/UTC and we convert at the edges).
 */

/** First day (00:00 local) of the month containing `d`. */
export function startOfMonth(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

/** First day of the month `n` months from `d` (n may be negative). */
export function addMonths(d: Date, n: number): Date {
  return new Date(d.getFullYear(), d.getMonth() + n, 1);
}

/** Stable `yyyy-MM-dd` key for a date in LOCAL time (not UTC). */
export function dayKey(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * A stable 6-week (42-day) grid of Dates covering the month that contains
 * `cursor`, starting on the Sunday on/before the 1st. Always 6 rows so the
 * calendar height doesn't jump between months.
 */
export function monthMatrix(cursor: Date): Date[][] {
  const first = startOfMonth(cursor);
  const start = new Date(first);
  start.setDate(first.getDate() - first.getDay()); // back up to Sunday
  const weeks: Date[][] = [];
  const run = new Date(start);
  for (let w = 0; w < 6; w++) {
    const row: Date[] = [];
    for (let i = 0; i < 7; i++) {
      row.push(new Date(run));
      run.setDate(run.getDate() + 1);
    }
    weeks.push(row);
  }
  return weeks;
}

/**
 * The ISO `from`/`to` window covering the full visible grid (so the API read
 * returns every post that could land in any rendered cell). `from` is the
 * start (00:00) of the first cell, `to` is the end (23:59:59.999) of the last.
 */
export function gridRange(cursor: Date): { fromISO: string; toISO: string } {
  const m = monthMatrix(cursor);
  const firstCell = m[0]![0]!;
  const lastCell = m[5]![6]!;
  const from = new Date(firstCell);
  from.setHours(0, 0, 0, 0);
  const to = new Date(lastCell);
  to.setHours(23, 59, 59, 999);
  return { fromISO: from.toISOString(), toISO: to.toISOString() };
}

/**
 * Group rows by their LOCAL day key, each day sorted chronologically. Generic
 * over anything with an ISO `scheduled_for`.
 */
export function groupByDayKey<T extends { scheduled_for: string }>(
  rows: readonly T[],
): Map<string, T[]> {
  const map = new Map<string, T[]>();
  for (const r of rows) {
    const k = dayKey(new Date(r.scheduled_for));
    const arr = map.get(k);
    if (arr) arr.push(r);
    else map.set(k, [r]);
  }
  for (const arr of map.values()) {
    arr.sort(
      (a, b) =>
        new Date(a.scheduled_for).getTime() - new Date(b.scheduled_for).getTime(),
    );
  }
  return map;
}

/** "June 2026"-style label for the month containing `cursor`. */
export function monthLabel(cursor: Date): string {
  return cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
}

/** "Mon, Jun 16"-style label for a single day (the agenda heading). */
export function longDayLabel(d: Date): string {
  return d.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

/** Local `HH:mm` for an ISO timestamp. */
export function localTime(iso: string): string {
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Split an ISO timestamp into the `{ date: yyyy-MM-dd, time: HH:mm }` pair the
 * shared `<DatePicker>` / `<TimePicker>` expect (both LOCAL).
 */
export function isoToLocalParts(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return { date: dayKey(d), time: `${hh}:${mm}` };
}

/**
 * Combine a LOCAL `yyyy-MM-dd` date + `HH:mm` time into a UTC ISO string for
 * the API. Returns null if either part is malformed.
 */
export function localPartsToIso(date: string, time: string): string | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date) || !/^\d{2}:\d{2}$/.test(time)) return null;
  const [y, m, d] = date.split("-").map(Number);
  const [hh, mm] = time.split(":").map(Number);
  const dt = new Date(y!, m! - 1, d!, hh!, mm!, 0, 0);
  if (Number.isNaN(dt.getTime())) return null;
  return dt.toISOString();
}
